import { apiv1, auth } from '@/adapters';
import { extractAccepted, extractBoolean } from '@/adapters/shared';

export interface UserIdentity {
  userID: string;
  email: string;
  name: string;
}

export const userService = {
  /**
   * Get the current authenticated user's identity
   */
  async getCurrentUser(): Promise<UserIdentity | null> {
    try {
      const res = await auth.getCurrentUser();
      if (res.ok && res.data) {
        return {
          userID: res.data.user_id ?? '',
          email: res.data.email ?? '',
          name: res.data.name ?? '',
        };
      }
      return null;
    } catch (err) {
      // 401/403 means not authenticated
      if (
        err instanceof Error &&
        (err.message.includes('401') || err.message.includes('403'))
      ) {
        return null;
      }
      throw err;
    }
  },

  /**
   * Add an email address to the current user's profile
   */
  async addEmail(email: string): Promise<boolean> {
    const res = await apiv1.addMyEmail({ email });
    return extractAccepted(res);
  },

  /**
   * Request verification for an email address (sends OTP)
   */
  async requestEmailVerification(email: string): Promise<boolean> {
    const res = await apiv1.verifyMyEmail({ email });
    return extractAccepted(res);
  },

  /**
   * Verify an email address with OTP code
   */
  async verifyEmailWithCode(email: string, code: string): Promise<boolean> {
    const res = await apiv1.verifyEmail({ email, code });
    return extractAccepted(res);
  },

  /**
   * Update the current user's name
   */
  async updateName(firstName: string, lastName: string): Promise<boolean> {
    const res = await apiv1.updateMyName({
      first_name: firstName,
      last_name: lastName,
    });
    return extractAccepted(res);
  },

  /**
   * Update the current user's profile photo
   */
  async updatePhoto(photoUrl: string): Promise<boolean> {
    const res = await apiv1.updateMyPhoto({ photo_url: photoUrl });
    return extractAccepted(res);
  },

  /**
   * Remove the current user's profile photo
   */
  async removePhoto(): Promise<boolean> {
    const res = await apiv1.removeMyPhoto();
    return extractBoolean(res);
  },
};
